/**
 * 账户设置：展示/编辑昵称、真实姓名、手机号、邮箱；头像单独上传；用户名只读。
 * 编辑、取消、保存按钮位于表单下方。
 */
import { useState, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { updateUserInfo } from '@hs/shared/stores/slices/authSlice';
import { trpc } from '@/lib/trpc';
import { Avatar, Button, Card, Input, Label, Separator, TextField } from '@heroui/react';
import { AvatarUploadDialog } from '@/components/AvatarUploadDialog';

function getUserInitial(user: { username?: string; nickname?: string; realname?: string } | null): string {
  if (!user) return 'U';
  const name = user.nickname?.trim() || user.realname?.trim() || user.username?.trim();
  if (!name) return 'U';
  return name.charAt(0);
}

function displayValue(value: string | null | undefined): string {
  if (value == null || String(value).trim() === '') return '未设置';
  return value;
}

export function AccountSettings() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const userId = user?.id;

  const [isEditing, setIsEditing] = useState(false);
  const [editNickname, setEditNickname] = useState('');
  const [editRealname, setEditRealname] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [avatarUploadOpen, setAvatarUploadOpen] = useState(false);

  const refreshUserInfoQuery = trpc.auth.refreshUserInfo.useQuery(undefined, {
    enabled: false,
  });

  const updateMyProfileMutation = trpc.auth.updateMyProfile.useMutation({
    onSuccess: async () => {
      setIsEditing(false);
      const refreshed = await refreshUserInfoQuery.refetch();
      if (refreshed.data) {
        const userData = refreshed.data as Parameters<typeof updateUserInfo>[0]['user'];
        dispatch(updateUserInfo({ user: userData }));
      }
    },
  });

  const handleStartEdit = useCallback(() => {
    if (user) {
      setEditNickname(user.nickname ?? '');
      setEditRealname(user.realname ?? '');
      setEditMobile(user.mobile ?? '');
      setEditEmail(user.email ?? '');
      setIsEditing(true);
    }
  }, [user]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!userId) return;
    updateMyProfileMutation.mutate({
      nickname: editNickname.trim() || undefined,
      realname: editRealname.trim() || undefined,
      mobile: editMobile.trim() || undefined,
      email: editEmail.trim() || undefined,
    });
  }, [userId, editNickname, editRealname, editMobile, editEmail, updateMyProfileMutation]);

  const handleAvatarConfirm = useCallback(
    (url: string) => {
      if (!userId) return;
      updateMyProfileMutation.mutate({ avatarUrl: url });
    },
    [userId, updateMyProfileMutation]
  );

  if (!userId || !user) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto p-6">
        <Card className="mx-auto w-full max-w-2xl">
          <Card.Content className="p-6">
            <p className="text-sm text-muted">请先登录</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <Card className="mx-auto w-full max-w-3xl">
        <Card.Content className="flex flex-col gap-0 p-4">
          {/* 头像 */}
          <div className="flex items-center justify-between gap-4 py-4">
            <Label className="text-sm font-medium text-foreground">头像</Label>
            <div className="flex items-center gap-3">
              <Avatar className="size-14 shrink-0" variant="soft">
                {user.avatarUrl ? (
                  <Avatar.Image src={user.avatarUrl} alt={user.username ?? ''} />
                ) : null}
                <Avatar.Fallback className="text-base font-semibold">{getUserInitial(user)}</Avatar.Fallback>
              </Avatar>
              <Button variant="ghost" size="sm" onPress={() => setAvatarUploadOpen(true)}>
                上传
              </Button>
              <AvatarUploadDialog
                open={avatarUploadOpen}
                onOpenChange={setAvatarUploadOpen}
                title="上传头像"
                onConfirm={handleAvatarConfirm}
              />
            </div>
          </div>
          <Separator />

          {/* 用户名（只读） */}
          <div className="flex items-center justify-between gap-4 py-4">
            <Label className="text-sm font-medium text-foreground">用户名</Label>
            <span className="text-sm text-muted">{user.username ?? '—'}</span>
          </div>
          <Separator />

          {/* 昵称 */}
          <div className="flex min-h-[52px] items-center justify-between gap-4 py-4">
            <Label className="shrink-0 text-sm font-medium text-foreground">昵称</Label>
            {isEditing ? (
              <TextField className="w-full max-w-[280px]">
                <Input
                  placeholder="请输入昵称"
                  value={editNickname}
                  onChange={(e) => setEditNickname(e.target.value)}
                />
              </TextField>
            ) : (
              <span className="text-sm text-muted">{displayValue(user.nickname)}</span>
            )}
          </div>
          <Separator />

          {/* 真实姓名 */}
          <div className="flex min-h-[52px] items-center justify-between gap-4 py-4">
            <Label className="shrink-0 text-sm font-medium text-foreground">真实姓名</Label>
            {isEditing ? (
              <TextField className="w-full max-w-[280px]">
                <Input
                  placeholder="请输入真实姓名"
                  value={editRealname}
                  onChange={(e) => setEditRealname(e.target.value)}
                />
              </TextField>
            ) : (
              <span className="text-sm text-muted">{displayValue(user.realname)}</span>
            )}
          </div>
          <Separator />

          {/* 手机号 */}
          <div className="flex min-h-[52px] items-center justify-between gap-4 py-4">
            <Label className="shrink-0 text-sm font-medium text-foreground">手机号</Label>
            {isEditing ? (
              <TextField className="w-full max-w-[280px]">
                <Input
                  placeholder="请输入手机号"
                  value={editMobile}
                  onChange={(e) => setEditMobile(e.target.value)}
                />
              </TextField>
            ) : (
              <span className="text-sm text-muted">{displayValue(user.mobile)}</span>
            )}
          </div>
          <Separator />

          {/* 邮箱 */}
          <div className="flex min-h-[52px] items-center justify-between gap-4 py-4">
            <Label className="shrink-0 text-sm font-medium text-foreground">邮箱</Label>
            {isEditing ? (
              <TextField className="w-full max-w-[280px]">
                <Input
                  placeholder="请输入邮箱"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              </TextField>
            ) : (
              <span className="text-sm text-muted">{displayValue(user.email)}</span>
            )}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onPress={handleCancel}>
                  取消
                </Button>
                <Button
                  variant="primary"
                  onPress={handleSave}
                  isDisabled={updateMyProfileMutation.isPending}
                >
                  保存
                </Button>
              </>
            ) : (
              <Button variant="primary" onPress={handleStartEdit}>
                编辑
              </Button>
            )}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
}
